import { AggregateField } from './events.js';

export const SHARE_THEMES = ['dark', 'light', 'brand'] as const;
export type ShareTheme = (typeof SHARE_THEMES)[number];

export const SHARE_ACCENTS = [
  { label: 'Blue', color: '#057BFA' },
  { label: 'Teal', color: '#14B8A6' },
  { label: 'Amber', color: '#F59E0B' },
  { label: 'Violet', color: '#8B5CF6' },
] as const;

// The breakdowns a public share may show: pageview aggregates only. Company, signal and visitor fields
// are never offered, so a share can't name who visited.
export const SHARE_CARDS = {
  pages: { title: 'Top Pages', column: 'Page', field: AggregateField.url },
  sources: {
    title: 'Top Sources',
    column: 'Source',
    field: AggregateField.referrer,
  },
  channels: {
    title: 'Channels',
    column: 'Channel',
    field: AggregateField.acquisition_channel,
  },
  countries: {
    title: 'Countries',
    column: 'Country',
    field: AggregateField.location_country,
  },
  devices: { title: 'Devices', column: 'Device', field: AggregateField.device },
  browsers: {
    title: 'Browsers',
    column: 'Browser',
    field: AggregateField.browser,
  },
} as const;
export type ShareCard = keyof typeof SHARE_CARDS;
export const SHARE_CARD_IDS = Object.keys(SHARE_CARDS) as ShareCard[];
export const SHARE_CARD_ROWS = 5;

export const isShareCard = (id: string): id is ShareCard =>
  (SHARE_CARD_IDS as string[]).includes(id);

// Tiles a share owner can hide: the two traffic metrics, and each shared event as `event:<name>`.
export const SHARE_METRICS = ['users', 'pageviews'] as const;

// A stored URL can carry query strings (emails, tokens), so a share shows page paths and referrer domains.
const shareValue = (card: ShareCard, value: string | null | undefined) => {
  if (!value) return value;
  if (card === 'pages') return value.split(/[?#]/)[0];
  if (card === 'sources') {
    const authority = value.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').split(/[/?#]/)[0]!;
    return authority.slice(authority.lastIndexOf('@') + 1);
  }
  return value;
};

export const shareCardRows = (
  card: ShareCard,
  rows: { property?: string | null; count: string | number }[],
) => {
  const counts = new Map<string | null | undefined, number>();
  for (const row of rows) {
    const key = shareValue(card, row.property);
    counts.set(key, (counts.get(key) ?? 0) + Number(row.count));
  }
  return [...counts]
    .map(([property, count]) => ({ property, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, SHARE_CARD_ROWS);
};
